import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './OnboardingWizard.css';

// Updated Step Components for enhanced email-first flow
import BasicInformationComplete from './steps/BasicInformationComplete';
import EmailVerificationStep from './steps/EmailVerificationStep';
import PabauSetupStep from './steps/PabauAutomationSetup';
import AccountSetupFlow from './steps/AccountSetupFlow';

// Components
import StepIndicator from './components/ProgressBar';
import ErrorHandler from './components/ErrorHandler';

// Hooks
import { useOnboardingSession } from './hooks/useOnboardingSession';

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pabauConfigData, setPabauConfigData] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({ score: null, isAcceptable: false });
  
  // Preload payment intent for faster step 3 loading
  const [paymentPreload, setPaymentPreload] = useState({
    isPreloading: false,
    isReady: false,
    clientSecret: null,
    setupIntentId: null,
    error: null
  });
  
  // Enhanced onboarding session management
  const {
    leadId,
    currentFlow,
    leadData,
    planData,
    billingConfig,
    validationErrors,
    updateLeadData,
    updatePlanData,
    createSignupLead,
    verifyEmail,
    configurePabauApiKey,
    selectPlan,
    createCheckoutSession,
    getLeadInfo
  } = useOnboardingSession();

  const isFreeMode = billingConfig?.freeModeEnabled || false;
  const freeModeTrialDays = billingConfig?.freeModeTrialDays || 60;
  const freeModePlanId = billingConfig?.freeModePlanId || 'free';

  // Get the correct API base URL
  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    } else {
      return process.env.REACT_APP_API_URL || '/api';
    }
  };

  // Preload payment intent for faster step 3 loading
  const preloadPaymentIntent = async () => {
    if (isFreeMode) {
      setPaymentPreload({
        isPreloading: false,
        isReady: true,
        clientSecret: null,
        setupIntentId: null,
        error: null
      });
      return;
    }

    if (!leadId || paymentPreload.isPreloading || paymentPreload.isReady) {
      return; // Already preloading or ready
    }

    console.log('Preloading payment intent for lead:', leadId);
    setPaymentPreload(prev => ({ ...prev, isPreloading: true, error: null }));

    try {
      const response = await fetch(`${getApiBaseUrl()}/onboarding/create-payment-intent/${leadId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.success) {
        setPaymentPreload({
          isPreloading: false,
          isReady: true,
          clientSecret: result.client_secret,
          setupIntentId: result.setup_intent_id,
          error: null
        });
        console.log('Payment intent preloaded successfully');
      } else {
        setPaymentPreload(prev => ({
          ...prev,
          isPreloading: false,
          error: result.message || 'Failed to preload payment'
        }));
        console.error('Failed to preload payment intent:', result);
      }
    } catch (error) {
      setPaymentPreload(prev => ({
        ...prev,
        isPreloading: false,
        error: 'Network error while preloading payment'
      }));
      console.error('Error preloading payment intent:', error);
    }
  };

  // Enhanced flow has 3 main steps:
  // 1. Complete Setup - Enter clinic details and create account (email already verified)
  // 2. Connect Pabau - Configure Pabau integration (required)
  // 3. Start Trial - Setup payment for 14-day free trial
  const totalSteps = 3;

  // Updated Step configuration for enhanced email-first flow
  const steps = [
    {
      id: 1,
      title: "Complete Setup",
      description: "Complete your clinic details and create your account",
      component: BasicInformationComplete,
      required: ['clinic_name', 'password', 'admin_name']
    },
    {
      id: 2,
      title: "Connect Pabau",
      description: "Configure your Pabau integration (required)",
      component: PabauSetupStep,
      required: ['pabau_credentials']
    },
    {
      id: 3,
      title: isFreeMode ? "Activate Free Plan" : "Start Trial",
      description: isFreeMode
        ? "Activate your workspace without entering payment details"
        : "Complete payment setup and activate your trial",
      component: AccountSetupFlow,
      required: []
    }
  ];

  // Handle plan selection from pricing page
  useEffect(() => {
    const planParam = searchParams.get('plan');
    const billingParam = searchParams.get('billing');
    
    if (planParam) {
      // console.log('Plan selected from pricing page:', planParam, 'billing:', billingParam);
      updatePlanData({
        plan_id: planParam,
        billing_period: billingParam || 'monthly'
      });
    }
  }, [searchParams, updatePlanData]);

  // Handle URL step parameter (from email verification OR dev mode)
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const leadIdParam = searchParams.get('lead_id');
    const devModeParam = searchParams.get('dev_mode');
    
    // Allow step parameter processing if:
    // 1. Coming from email verification (has both step and lead_id)
    // 2. Dev mode (has step and dev_mode=true)
    if (stepParam && (leadIdParam || devModeParam === 'true')) {
      const stepNumber = parseInt(stepParam, 10);
      // console.log('URL step parameter:', stepParam, 'Lead ID:', leadIdParam, 'Dev Mode:', devModeParam);
      
      // If coming from email verification or dev mode, start at the specified step
      if (stepNumber >= 1 && stepNumber <= totalSteps) {
        setCurrentStep(stepNumber);
      }
    }
  }, [searchParams, totalSteps]);

  // Handle different flow states (only if not coming from URL step parameter)
  useEffect(() => {
    const stepParam = searchParams.get('step');
    const leadIdParam = searchParams.get('lead_id');
    const devModeParam = searchParams.get('dev_mode');
    
    // Don't override step if coming from URL parameters (email verification or dev mode)
    if (stepParam && (leadIdParam || devModeParam === 'true')) {
      return;
    }
    
    // console.log('Current flow state:', currentFlow, 'Lead ID:', leadId);
    
    switch (currentFlow) {
      case 'verification_sent':
      case 'verification_resent':
        // Show email verification step
        setCurrentStep('email_verification');
        break;
      case 'verified':
        // Move to step 1 (account setup) after email verification
        setCurrentStep(1);
        break;
      case 'pabau_configured':
        // Move to payment step (plan already selected from pricing page)
        setCurrentStep(3);
        break;
      case 'payment_pending':
        // Show payment processing or redirect
        setCurrentStep(3);
        break;
      case 'completed':
        // Redirect to success page or dashboard
        navigate('/onboarding/success');
        break;
    }
  }, [currentFlow, leadId, navigate, searchParams]);

  const currentStepConfig = steps.find(step => step.id === currentStep);

  // Enhanced navigation handlers
  const handleNext = async () => {
    // console.log('handleNext called, currentStep:', currentStep, 'currentFlow:', currentFlow);
    
    // Prevent duplicate calls
    if (isLoading) {
      // console.log('Already processing, ignoring duplicate call');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);

      if (currentStep === 1) {
        // Step 1: Account setup (either new lead or update existing verified lead)
        // console.log('Step 1: Account setup with data:', leadData);
        
        // Client-side validation (email already verified)
        const errors = {};
        
        if (!leadData.clinic_name || leadData.clinic_name.length < 2) {
          errors.clinic_name = 'Please enter a clinic name (at least 2 characters)';
        }
        
        if (!leadData.password || leadData.password.length < 8) {
          errors.password = 'Password must be at least 8 characters long';
        } else if (passwordStrength.score !== null && !passwordStrength.isAcceptable) {
          errors.password = 'Password is too weak. Please choose a stronger password (zxcvbn score 3+ required)';
        }
        // Note: Password strength is validated by zxcvbn in the PasswordInput component
        // Stytch requires zxcvbn score ≥ 3, no LUDS requirements needed
        
        if (!leadData.admin_name || leadData.admin_name.length < 2) {
          errors.admin_name = 'Please enter your name (at least 2 characters)';
        }
        
        if (!leadData.password_confirm) {
          errors.password_confirm = 'Please confirm your password';
        } else if (leadData.password !== leadData.password_confirm) {
          errors.password_confirm = 'Passwords do not match';
        }
        
        if (Object.keys(errors).length > 0) {
          const errorMessages = Object.values(errors);
          const errorSummary = errorMessages.length === 1 
            ? errorMessages[0] 
            : `Please fix the following: ${errorMessages.join('; ')}`;
          setError(errorSummary);
          // Update validation errors so they show inline
          updateLeadData({ validation_errors: errors });
          return;
        }
        
        // Clear validation errors if validation passes
        if (leadData.validation_errors) {
          updateLeadData({ validation_errors: {} });
        }
        
        // Check if this is a verified lead (from email verification)
        const leadIdParam = searchParams.get('lead_id');
        if (leadIdParam) {
          // Coming from email verification - complete clinic setup with Stytch
          // console.log('Completing clinic setup for verified lead:', leadIdParam);
          
          try {
            const response = await fetch(`${getApiBaseUrl()}/onboarding/complete-clinic-setup/${leadIdParam}`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                clinic_name: leadData.clinic_name,
                password: leadData.password,
                admin_name: leadData.admin_name,
                contact_phone: leadData.contact_phone || '',
                timezone: leadData.timezone || 'Europe/London',
                currency: leadData.currency || 'GBP'
              })
            });
            
            const result = await response.json();
            
            if (result.success) {
              // console.log('Clinic setup completed successfully');
              setCurrentStep(2);
            } else {
              setError(result.detail || 'Failed to complete clinic setup');
              return;
            }
          } catch (err) {
            console.error('Failed to complete clinic setup:', err);
            setError('Failed to complete clinic setup. Please try again.');
            return;
          }
        } else {
          // New lead creation flow
          const result = await createSignupLead(leadData);
          // console.log('Signup lead result:', result);
          
          if (result.success) {
            // Handle different response actions
            switch (result.action) {
              case 'verification_sent':
              case 'verification_resent':
                // Show email verification step
                setCurrentStep('email_verification');
                break;
              case 'redirect_to_plans':
                // User already verified, go to step 2
                setCurrentStep(2);
                break;
              case 'choose_clinic':
                // Show clinic choice dialog
                setError(`You have an existing signup for '${result.existing_clinic}'. Would you like to continue with that or start fresh?`);
                break;
              default:
                setError(result.message || 'Unexpected response from server');
            }
          } else {
            setError(result.error || 'Failed to create signup lead');
          }
        }
        return;
      }

      // Step 2 is now handled by PabauSetupStep component internally
      // No need for manual validation here

      // For other steps, just move forward
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    } catch (err) {
      console.error('handleNext error:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 'email_verification') {
      setCurrentStep(1);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
    setError(null);
  };

  const handleStepClick = (stepNumber) => {
    // Allow jumping to completed steps only
    if (stepNumber <= currentStep && typeof currentStep === 'number') {
      setCurrentStep(stepNumber);
      setError(null);
    }
  };

  const handleResendVerification = async () => {
    if (!leadId) {
      setError('No lead ID found. Please start over.');
      return;
    }
    
    try {
      setIsLoading(true);
      const response = await fetch(`${getApiBaseUrl()}/onboarding/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: leadId,
          email: leadData.email
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setError(null);
        // Return the result so the component can handle success state
        return result;
      } else {
        setError(result.message || 'Failed to resend verification email');
        return result;
      }
    } catch (err) {
      setError('Failed to resend verification email');
      return { success: false, error: 'Network error' };
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivateFreeMode = useCallback(async () => {
    if (!leadId) {
      return {
        success: false,
        error: 'No lead ID found. Please restart onboarding.'
      };
    }

    try {
      if (planData && planData.plan_id) {
        const planResult = await selectPlan(planData);
        if (!planResult.success) {
          return {
            success: false,
            error: planResult.error || 'Failed to save your plan selection'
          };
        }
      }

      const response = await fetch(`${getApiBaseUrl()}/onboarding/activate-free-mode/${leadId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.detail || result.message || 'Failed to activate complimentary access'
        };
      }

      await getLeadInfo(leadId);
      return { success: true, ...result };
    } catch (err) {
      console.error('Free mode activation failed:', err);
      return {
        success: false,
        error: err.message || 'Unable to activate complimentary access'
      };
    }
  }, [leadId, planData, selectPlan, getLeadInfo]);

  // Complete Setup Step (Payment) - simple function without useCallback to avoid dependency issues
  const handleCreateCheckout = async () => {
    if (isFreeMode) {
      return handleActivateFreeMode();
    }

    // Prevent multiple calls when already processing
    if (isLoading) {
      // console.log('Already processing checkout, ignoring duplicate call');
      return;
    }
    
    // Create checkout session for the selected plan
    try {
      setIsLoading(true);
      setError(null);
      
      // First ensure plan is selected
      if (planData && planData.plan_id) {
        const planResult = await selectPlan(planData);
        if (!planResult.success) {
          setError(planResult.error || 'Failed to select plan');
          return;
        }
      }
      
      // Create checkout session
      const checkoutResult = await createCheckoutSession();
      if (!checkoutResult.success) {
        setError(checkoutResult.error || 'Failed to create checkout session');
      }
      // If successful, Stripe redirect will happen automatically
    } catch (err) {
      setError('Failed to start checkout process');
    } finally {
      setIsLoading(false);
    }
  };

  // Special handling for email verification step
  if (currentStep === 'email_verification') {
    return (
      <div className="onboarding-wizard">
        <div className="onboarding-container">
          <div className="progress-bar-container">
            <StepIndicator
              currentStep={1.5} // Between step 1 and 2
              steps={steps}
            />
          </div>

          <div className="onboarding-content">
            <EmailVerificationStep
              email={leadData.email}
              clinicName={leadData.clinic_name}
              onResend={handleResendVerification}
              onBack={() => setCurrentStep(1)}
              isLoading={isLoading}
              error={error}
            />
          </div>
        </div>
      </div>
    );
  }

  // Render current step component
  const renderStepComponent = () => {
    const StepComponent = currentStepConfig.component;
    
    if (currentStep === 1) {
      return (
        <StepComponent
          data={leadData}
          onUpdate={updateLeadData}
          onNext={handleNext}
          onPrevious={handlePrevious}
          isLoading={isLoading}
          error={error}
          validationErrors={leadData.validation_errors || {}}
          onPasswordStrengthChange={setPasswordStrength}
        />
      );
    } else if (currentStep === 2) {
      // Pabau Automation Setup Step
      return (
        <StepComponent
          leadId={leadId}
          onComplete={() => {
            // Preload payment intent before moving to step 3
            preloadPaymentIntent();
            setCurrentStep(3);
          }}
          onBack={handlePrevious}
        />
      );
    } else if (currentStep === 3) {
      // Complete Setup Step (Payment) - using handleCreateCheckout from top level
      return (
        <StepComponent
          leadData={leadData}
          planData={planData}
          leadId={leadId}
          currentFlow={currentFlow}
          onPrevious={handlePrevious}
          onCreateCheckout={handleCreateCheckout}
          onActivateFreeMode={handleActivateFreeMode}
          isFreeMode={isFreeMode}
          billingConfig={billingConfig}
          isLoading={isLoading}
          error={error}
          paymentPreload={paymentPreload}
        />
      );
    }
  };

  // Show navigation buttons
  const showPrevious = currentStep > 1 && typeof currentStep === 'number' && currentStep !== 2; // Hide for step 2 (has own buttons)
  const showNext = currentStep < 3 && typeof currentStep === 'number' && currentStep !== 2; // Hide for step 2 (has own buttons)

  return (
    <div className="onboarding-wizard">
      <div className="onboarding-container">
        {/* Step Indicator */}
        <div className="progress-bar-container">
          <StepIndicator
            currentStep={currentStep}
            steps={steps}
          />
        </div>

        {/* Main Content */}
        <div className="onboarding-content">
          {/* Step Header - Hide for step 2 (Pabau setup) */}
          {currentStepConfig && currentStep !== 2 && (
            <div className="step-header">
              <h1 className="step-title">{currentStepConfig.title}</h1>
              <p className="step-description">{currentStepConfig.description}</p>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <ErrorHandler
              error={error}
              onRetry={() => setError(null)}
            />
          )}

          {/* Step Component */}
          <div className="step-content">
            {renderStepComponent()}
          </div>

          {/* Navigation Buttons */}
          <div className="onboarding-navigation">
            {showPrevious && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handlePrevious}
                disabled={isLoading}
              >
                Previous
              </button>
            )}

            {showNext && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 
                 currentStep === 1 ? 'Continue to Pabau Setup' :
                 currentStep === 2 ? 'Continue to Payment' :
                 'Next'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard; 
